import React, { useRef } from 'react';
import { SortableContainer, SortableElement, sortableHandle } from 'react-sortable-hoc';
import { getBadgeClass, getBadgeLabel } from './conversationFilters';

const DragHandle = sortableHandle(() => (
  <span className="badge-drag-handle">
    <i className="dragicon">
      <img src="/images/dragbtn.png" alt="" />
    </i>
  </span>
));

function useBadgeHandlers(onToggleVisibility, onToggleHasData) {
  const clickTimerRef = useRef(null);

  const handleClick = (variable) => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      onToggleVisibility(variable);
      clickTimerRef.current = null;
    }, 220);
  };

  const handleDoubleClick = (variable, e) => {
    e.preventDefault();
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    onToggleHasData(variable);
  };

  return { handleClick, handleDoubleClick };
}

const SortableBadgeItem = SortableElement(
  ({ variable, badgeClass, label, title, onClick, onDoubleClick, sortable }) => (
    <div className="variable-badge-wrap">
      {sortable && <DragHandle />}
      <button
        type="button"
        className={badgeClass}
        title={title}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        {label}
      </button>
    </div>
  ),
);

const BadgeListContent = ({
  items,
  hiddenColumns,
  hasDataVars,
  onToggleVisibility,
  onToggleHasData,
  sortable,
  t,
}) => {
  const { handleClick, handleDoubleClick } = useBadgeHandlers(onToggleVisibility, onToggleHasData);
  const title = t('conversation.badgeTooltip');

  return (
    <div className="variable-badges">
      {items.map((variable, index) => {
        const badgeClass = getBadgeClass(variable, hiddenColumns, hasDataVars);
        const label = getBadgeLabel(variable, t);
        const onClick = () => handleClick(variable);
        const onDoubleClick = (e) => handleDoubleClick(variable, e);

        if (sortable) {
          return (
            <SortableBadgeItem
              key={variable}
              index={index}
              variable={variable}
              sortable={sortable}
              badgeClass={badgeClass}
              label={label}
              title={title}
              onClick={onClick}
              onDoubleClick={onDoubleClick}
            />
          );
        }

        return (
          <div key={variable} className="variable-badge-wrap">
            <button
              type="button"
              className={badgeClass}
              title={title}
              onClick={onClick}
              onDoubleClick={onDoubleClick}
            >
              {label}
            </button>
          </div>
        );
      })}
    </div>
  );
};

const SortableBadgeList = SortableContainer(BadgeListContent);

export const VariableBadgeFilter = ({
  variables = [],
  hiddenColumns = [],
  hasDataVars = [],
  onToggleVisibility,
  onToggleHasData,
  onReorder,
  sortable = false,
  t,
}) => {
  if (!variables.length) return null;

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    onReorder(oldIndex, newIndex);
  };

  const sharedProps = {
    items: variables,
    hiddenColumns,
    hasDataVars,
    onToggleVisibility,
    onToggleHasData,
    sortable,
    t,
  };

  return (
    <div className="filter filter-variable-badges">
      <span className="variable-badges-label">{t('conversation.collectableData')}</span>
      {sortable ? (
        <SortableBadgeList
          axis="xy"
          lockToContainerEdges
          useDragHandle
          helperClass="variable-badge-sortable-helper"
          distance={5}
          onSortEnd={handleSortEnd}
          {...sharedProps}
        />
      ) : (
        <BadgeListContent {...sharedProps} />
      )}
    </div>
  );
};
